import {
  Component,
  TemplateRef,
  input,
  booleanAttribute,
  contentChild,
  viewChild,
  signal,
  inject,
  computed,
  ChangeDetectionStrategy
} from '@angular/core';
import { toObservable, toSignal } from '@angular/core/rxjs-interop';
import { NgTemplateOutlet } from '@angular/common';
import { MatButton } from '@angular/material/button';
import { timer, Subject } from 'rxjs';
import {
  switchMap,
  map,
  tap,
  delayWhen,
  withLatestFrom,
  filter,
  pairwise,
  mergeMap
} from 'rxjs/operators';
import { Store, select } from '@ngrx/store';
import { SatPopoverAnchorDirective, SatPopoverComponent } from '@ncstate/sat-popover';

import {
  selectZefErrorsByType,
  zefRemoveError,
  ZefErrorTemplateService
} from '../errors';
import { ButtonProgressComponent, selectZefProgressesByType } from '../progress';
import { ZefReactiveComponentBase } from '../core';

@Component({
  // eslint-disable-next-line @angular-eslint/component-selector
  selector: 'zef-progress-error',
  templateUrl: './progress-error.container.html',
  styleUrls: [ './progress-error.container.scss' ],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    ButtonProgressComponent,
    SatPopoverAnchorDirective,
    SatPopoverComponent,
    NgTemplateOutlet
  ],
  host: {
    '[class.is-full]': 'full()'
  }
})
export class ProgressErrorContainer extends ZefReactiveComponentBase {

  // # Deps
  #store = inject(Store);
  #errorTemplates = inject(ZefErrorTemplateService);

  // # Event Streams
  onPopoverClosed$ = new Subject<void>();

  // # Data
  // -- sync
  tpl = signal<TemplateRef<any> | undefined>(undefined);

  // -- angular
  key = input<string | string []>();
  full = input(false, { transform: booleanAttribute });
  loading = input(false);
  disabled = input(false);
  disabledMode = input<'apply-both' | 'progress-only'>('apply-both');

  buttonRef = contentChild(MatButton);
  popoverAnchorRef = viewChild(SatPopoverAnchorDirective);

  #observableKey$ = toObservable(this.key);

  // -- async
  #progressActive$ = this.#observableKey$.pipe(
    filter((keyOrKeys) => !!keyOrKeys),
    switchMap((keyOrKeys) => this.#store.pipe(
      select(selectZefProgressesByType(keyOrKeys)),
    )),
    map((d) => !!d.length),
    delayWhen((v) => !v ? timer(200) : timer(0))
  );

  #errorData$ = this.#observableKey$.pipe(
    filter((keyOrKeys) => !!keyOrKeys),
    switchMap((keyOrKeys) => this.#store.pipe(
      select(selectZefErrorsByType(keyOrKeys)),
    ))
  );

  #errorsActive$ = this.#errorData$.pipe(
    map((d) => d.filter((i) => i.errorType === 'pop')),
    tap((errs) => {
      const popoverRef = this.popoverAnchorRef();
      if (popoverRef) {
        setTimeout(() => {
          this.tpl.set(this.#errorTemplates.getTemplate('pop'))
          if (errs && errs.length) {
            popoverRef.popover.open();
          } else {
            popoverRef.popover.close();
          }
        })
      }
    }),
    pairwise(),
    map(([ a, b ]) => a.length ? a : b),
    delayWhen((d) => !d?.length ? timer(100) : timer(0))
  );

  // -- signals
  progressActiveSignal = toSignal(this.#progressActive$, { initialValue: false });

  // -- computed
  effectiveDisabled = computed(() => {
    const mode = this.disabledMode();
    const disabled = this.disabled();
    const progressActive = this.progressActiveSignal();

    if (mode === 'progress-only' && progressActive) {
      return false;
    }
    return disabled;
  });

  // # State resolver
  state = this.$connect({
    errorData: this.#errorData$,
    errorsActive: this.#errorsActive$
  });

  constructor() {
    super();

    this.$dispatchActions([
      this.onPopoverClosed$.pipe(
        withLatestFrom(this.#errorData$),
        filter(([ _, errorData ]) => !!errorData.length),
        // Remove ALL errors that were displayed in the popover
        mergeMap(([ _, errorData ]) =>
          errorData.map((error) => zefRemoveError(error.key, error.id ? String(error.id) : undefined))
        )
      )
    ]);
  }
}
